import type { ReactNode } from 'react';

/**
 * ─── LAYER: lib ──────────────────────────────────────────────────────────────────
 * THE SECTION REGISTRY. How a long form's sections are ORDERED, STEPPED and GATED —
 * as data, in one array, so rearranging a screen is an edit to a literal rather than a
 * diff through the middle of working code.
 *
 * This file is pure: no JSX, no page import, no React beyond the `ReactNode` type. The
 * descriptors that use it live beside the pages that own them
 * (`pages/ArrangementBuilder/sections.tsx`, `pages/StandingOrderBuilder/sections.tsx`),
 * and `components/intake/IntakeForm.tsx` is the one renderer of both.
 *
 * ⚠️ THERE ARE TWO WAYS FOR A SECTION NOT TO BE ON SCREEN, AND THEY ARE NOT
 * INTERCHANGEABLE.
 *
 *   ABSENT FROM THE REGISTRY   not rendered at all; its fields are never registered.
 *                              This is how the round-the-clock form drops `command`.
 *   `visibleWhen` FALSE        rendered, still registered, still watched — wearing the
 *                              Tailwind `hidden` class.
 *
 * The second is not an implementation detail. `Form.useWatch`'s selector is called with
 * `getFieldsValue()`, which answers REGISTERED FIELDS ONLY (CLAUDE.md §8), so a section
 * that leaves the tree takes every derivation that reads its fields down to zero —
 * silently, with the grid, the checklist and the right rail all agreeing on the wrong
 * answer. A renderer must therefore `map` over EVERY descriptor and hide with a class.
 * `sections.filter(isSectionVisible).map(...)` reads better and is the bug.
 */

/**
 * ⚠️ THE P3a.2 DISTINCTION, CLIENT-SIDE — AND IT IS NOT A PUBLISH AUTHORITY.
 *
 * A BLOCKER leaves the thing being built undefined; a WARNING is information somebody
 * will come back to. What is decided HERE decides a tick in the right-hand checklist and
 * nothing else: the pill, the modal behind it and the Publish button's `disabled` read the
 * SERVER's preview and only that. Two validators, one display, and the server wins on the
 * one question that matters.
 */
export type SectionVerdict = {
  blockers: readonly string[];
  warnings: readonly string[];
  /** The one short phrase the checklist shows beside the tick. */
  detail?: string;
};

export type StepDescriptor<TStep extends string = string> = {
  key: TStep;
  title: string;
};

/**
 * ⚠️ `TState` IS THE PURE DATA; `TActions` IS THE IMPERATIVE SURFACE, AND THEY ARE TWO
 * PARAMETERS RATHER THAN ONE OBJECT FOR A REASON THAT A COMMENT COULD NOT HOLD.
 *
 * `visibleWhen` and `validate` receive only `TState`, so they CANNOT reach a form instance,
 * a ref or a mutation — the compiler says so, not a convention. It is also what lets
 * `checklistOf` run during render: handing it an object that closes over a ref is exactly
 * what `react-hooks/refs` refuses, and it is right to, because a checklist that depended on
 * a ref would silently stop updating.
 */
export type SectionDescriptor<TState, TStep extends string = string, TActions = unknown> = {
  /** ⚠️ THE DOM ID. Rendered as `section-<id>` and `heading-<id>`, and asserted as both. */
  id: string;
  /**
   * ⚠️ A STEP KEY, NEVER AN INDEX. Collapsing three steps into two must not renumber every
   * descriptor, and a key that no step declares is a compile error rather than a section
   * that silently never appears.
   */
  step: TStep;
  /** The `<h2>`. */
  title: string;
  /** What the checklist calls it, when the heading is too long for a nav row. */
  navTitle?: string;
  /** One sentence beside the heading. Absent renders nothing at all. */
  tooltip?: string;
  /** ⚠️ FALSE MEANS CLASS-HIDDEN, NEVER UNMOUNTED. See this file's header. */
  visibleWhen?: (state: TState) => boolean;
  validate: (state: TState) => SectionVerdict;
  /**
   * ⚠️ RETURNS ELEMENTS, AND IS CALLED RATHER THAN MOUNTED. Never put a hook in here: it
   * would run in the RENDERER's hook order, and would only misbehave on the day a registry
   * changes length. Put the hook in the section component, which is a real component.
   */
  render: (state: TState, actions: TActions) => ReactNode;
  /**
   * The control in the section's header row. ⚠️ Return `undefined` to render NOTHING — a
   * hidden or disabled button is a different thing and the suite can tell them apart.
   */
  action?: (state: TState, actions: TActions) => ReactNode;
  /**
   * ⚠️ [amended D4.1] WHETHER THE SECTION IS A CARD, OR SITS ON THE PAGE.
   *
   * `'card'` — the default, and what every section has always been: a bordered surface with
   * its heading inside it.
   *
   * `'bare'` — no border, no background, no padding. The heading row and the content sit
   * DIRECTLY on the page. It exists for a section whose content is itself a list of cards:
   * a card around a stack of cards is a box in a box, and it costs the inner one 34px of
   * measure on every side of the page for a rule nobody reads as meaning anything.
   *
   * ⚠️ IT IS DATA, NOT A CLASS PROP, for the reason the whole registry is data — a page
   * that wanted this by reaching into `IntakeForm`'s JSX would be the drift [P3b.1] removed.
   * `id`, the ref, `aria-labelledby` and the step's class-hide are identical either way, so
   * nothing that addresses a section by id can tell the two apart.
   */
  surface?: 'card' | 'bare';
  /**
   * ⚠️ [C1f §5] WHETHER THIS SECTION'S BODY SCROLLS INSIDE ITSELF, OR THE FORM SCROLLS PAST IT.
   *
   * `'flow'` — the default, and what every section has always been: content of its natural
   * height inside a form that scrolls as one.
   *
   * `'scroll'` — the section becomes a bounded column: its heading row is a `shrink-0`
   * sibling above a body that owns its own overflow. It exists for a section whose content is
   * an unbounded LIST — a stack of duty point cards whose header and `Add duty point` must
   * stay reachable at the fortieth card as at the first.
   *
   * ⚠️ IT IS DATA, LIKE `surface`, FOR THE SAME REASON. A page that wanted this by reaching
   * into `IntakeForm`'s JSX would be the drift [P3b.1] removed, and defaulting it to today's
   * behaviour is what keeps `StandingOrderBuilder` byte-identical.
   *
   * ⚠️ AT MOST ONE PER STEP. A second bounded body on one step would have to divide a height
   * neither descriptor knows about; `sectionRegistry.test.ts` refuses it, over both shipped
   * registries.
   */
  body?: 'flow' | 'scroll';
};

export type ChecklistItem = {
  id: string;
  label: string;
  done: boolean;
  detail?: string;
};

/** Absent `visibleWhen` means visible. The common case costs no property. */
export const isSectionVisible = <TState, TStep extends string, TActions>(
  descriptor: SectionDescriptor<TState, TStep, TActions>,
  state: TState,
): boolean => descriptor.visibleWhen?.(state) ?? true;

export const stepIndexOf = <TStep extends string>(steps: readonly StepDescriptor<TStep>[], key: TStep): number =>
  steps.findIndex((step) => step.key === key);

/**
 * Which step a section id sits on. `-1` for an id no descriptor claims — the same answer
 * `FORM_STEPS.findIndex(...)` gave before the registry existed, and it is never equal to a
 * step index, so an unknown id stays hidden rather than appearing on step 0.
 */
export const stepOfSection = <TState, TStep extends string, TActions>(
  sections: readonly SectionDescriptor<TState, TStep, TActions>[],
  steps: readonly StepDescriptor<TStep>[],
  id: string,
): number => {
  const found = sections.find((section) => section.id === id);
  return found ? stepIndexOf(steps, found.step) : -1;
};

export const isSectionHidden = <TState, TStep extends string, TActions>(
  descriptor: SectionDescriptor<TState, TStep, TActions>,
  steps: readonly StepDescriptor<TStep>[],
  currentStep: number,
  state: TState,
): boolean => stepIndexOf(steps, descriptor.step) !== currentStep || !isSectionVisible(descriptor, state);

/**
 * ⚠️ [C1f §5] DOES THE STEP ON SCREEN CONTAIN A BOUNDED BODY?
 *
 * `IntakeForm` asks this to decide whether to let its primary column FILL the row rather than
 * be sized by its content — which is what gives the bounded body a definite height to bound
 * against. A class-hidden section must not arm it: `display: none` contributes no height, so
 * a step with none of its own would reserve a row for nothing.
 *
 * ⚠️ IT IS A STATE BRANCH, NEVER A WIDTH ONE. §8 permits the first and forbids the second,
 * and `vitest.setup.ts` stubs `matchMedia` to `matches: false` for ever — so a width-branching
 * render would take the narrow path in every test, silently.
 */
export const stepHasScrollingBody = <TState, TStep extends string, TActions>(
  sections: readonly SectionDescriptor<TState, TStep, TActions>[],
  steps: readonly StepDescriptor<TStep>[],
  currentStep: number,
  state: TState,
): boolean =>
  sections.some(
    (descriptor) => descriptor.body === 'scroll' && !isSectionHidden(descriptor, steps, currentStep, state),
  );

/**
 * ⚠️ DONE IS "THE OPERATOR HAS DEALT WITH IT", WHICH IS BOTH LISTS AND NOT ONLY BLOCKERS.
 *
 * On blockers alone, `command` and `extras` would tick the moment the page opened — a
 * missing command name is a warning by design (P3a.2) and instructions are optional — and
 * a checklist whose rows are ticked before anything has been typed is a checklist nobody
 * reads twice.
 */
export const sectionDone = (verdict: SectionVerdict): boolean =>
  verdict.blockers.length === 0 && verdict.warnings.length === 0;

/** The checklist, in registry order, skipping sections that are not on screen at all. */
export const checklistOf = <TState, TStep extends string, TActions>(
  sections: readonly SectionDescriptor<TState, TStep, TActions>[],
  state: TState,
): ChecklistItem[] =>
  sections
    .filter((descriptor) => isSectionVisible(descriptor, state))
    .map((descriptor) => {
      const verdict = descriptor.validate(state);
      return {
        id: descriptor.id,
        label: descriptor.navTitle ?? descriptor.title,
        done: sectionDone(verdict),
        ...(verdict.detail === undefined ? {} : { detail: verdict.detail }),
      };
    });
